import { useEffect, useState } from "react";
import { Volume2, VolumeX, X, ArrowRight, ScanLine, Siren, Shield, ShieldAlert, Building2, MessageSquareWarning, PhoneCall, FileText, Bell, Heart, Users, Stethoscope } from "lucide-react";
import { Button } from "./ui/button";
import { enableUrgentNotifications, disableUrgentNotifications, isUrgentNotificationsEnabled } from "./UrgentNotifications";
import { speakFR } from "./ReadAloudFAB";
import AvocatEnOrLogo from "./AvocatEnOrLogo";
import GothicBanner from "./GothicBanner";
import { toast } from "sonner";

const TUTORIAL = [
  {
    title: "Bienvenue dans Un Avocat en Or",
    body: "Votre assistant juridique en cas d'urgence, 24 h/24, conçu spécialement pour la Belgique, la France, le Luxembourg et la Suisse romande.",
    hero: true,  // affiche logo + banderole gothique au lieu d'un emoji
  },
  {
    title: "⚡ ÉTAPE CRITIQUE — Préparez votre téléphone",
    body: "En cas d'urgence (contrôle de police, agression), chaque seconde compte. L'iPhone exige que vous autorisiez UNE SEULE FOIS le micro et la géolocalisation. Touchez le gros bouton ci-dessous MAINTENANT, au calme, pour ne plus jamais perdre de temps à les autoriser pendant un vrai contrôle.",
    permissions: true,
    visual: "⚡",
  },
  {
    title: "13 touches pour 13 situations",
    body: "L'écran d'accueil regroupe tous les outils dont vous pouvez avoir besoin. La tuile 1 est désormais l'Urgence Médicale — allergies, QR d'urgence, et notre toute nouvelle Consultation Sécurisée (on y revient dans un instant).",
    tiles: [
      { icon: Stethoscope, label: "1. Médical", desc: "Allergies + QR + Consultation sécurisée" },
      { icon: Siren, label: "2. Police", desc: "Contrôle + mail Comité P" },
      { icon: Shield, label: "3. Bouclier", desc: "Conflit OU protection" },
      { icon: Building2, label: "4. Bureau", desc: "Notaire, assureur, admin" },
      { icon: MessageSquareWarning, label: "5. Message", desc: "SMS / email suspect" },
      { icon: PhoneCall, label: "6. Appel", desc: "Voix clonée, deepfake" },
      { icon: ScanLine, label: "7. Scanner", desc: "Document → traduction + lettre" },
      { icon: ShieldAlert, label: "8. Travail", desc: "Harcèlement + syndicat" },
      { icon: Shield, label: "9. Animaux", desc: "Maltraitance, abandon" },
      { icon: Building2, label: "10. Étrangers", desc: "Sans-papiers — traduit + intelligence collaborative" },
      { icon: ScanLine, label: "11. Suggestion", desc: "Vos idées comptent" },
      { icon: FileText, label: "12. Officiel", desc: "Constat, litige, admin" },
      { icon: ShieldAlert, label: "13. Urgence vitale", desc: "Agression, LGBTQI+, Refuge" },
    ],
  },
  {
    title: "❤️ Un compteur honnête et transparent",
    body: "Sous le titre de la page d'accueil, vous voyez « X personnes aidées par notre service à ce jour ». Ce chiffre ne compte que les VRAIS utilisateurs — pas les comptes de test. Il monte à chaque fois qu'une personne réelle utilise une touche. Vous pouvez le partager en un clic pour lancer le bouche-à-oreille.",
    visual: "❤️",
  },
  {
    title: "👥 Vous n'êtes pas seul·e",
    body: "Sur chaque tuile, un petit badge affiche combien de personnes ont vécu la MÊME situation que vous : contrôle de police abusif, harcèlement au travail, maltraitance animale... Vous pouvez rejoindre une plainte collective : votre témoignage, ajouté aux autres, fait pression sur les autorités et augmente vos chances de justice.",
    visual: "👥",
  },
  {
    title: "🩺 Consultation sécurisée — protection allergies",
    body: "Dans la tuile Médical, un 4ᵉ onglet révolutionnaire : avec l'accord de votre médecin, l'app écoute EN DIRECT la prescription. Dès que le médecin prononce un composant auquel vous êtes allergique (pénicilline, aspirine, arachide...), votre téléphone VIBRE FORT et affiche une alerte rouge : « Attention, risque d'allergie détecté ! ». Vous pouvez alerter le médecin avant qu'il ne signe l'ordonnance. Cela peut vous sauver la vie.",
    visual: "🩺",
  },
  {
    title: "🪔 La lampe d'Aladin",
    body: "Vous ne savez pas quelle touche utiliser ? Frottez la lampe en bas de l'écran et expliquez votre situation à voix haute. L'IA vous emmène à la bonne fonction.",
    visual: "🪔",
  },
  {
    title: "Accessibilité — pour tous",
    body: "Le bouton 🔊 doré en bas à droite lit le contenu à voix haute. Pour activer la lecture automatique à chaque page, ouvrez l'onglet « Accessibilité » de ce guide.",
    visual: "🔊",
  },
];

export default function HelpAndAccessibility({ open, onClose, initialTab = "tutorial" }) {
  const [tab, setTab] = useState(initialTab);
  const [step, setStep] = useState(0);
  const [autoRead, setAutoRead] = useState(false);
  const [speechRate, setSpeechRate] = useState(1.0);
  const [urgentNotifs, setUrgentNotifs] = useState(false);

  useEffect(() => {
    if (open) {
      setTab(initialTab);
      setStep(0);
      try {
        setAutoRead(localStorage.getItem("accessibility_auto_read") === "1");
        const r = parseFloat(localStorage.getItem("accessibility_speech_rate") || "1.0");
        setSpeechRate(isNaN(r) ? 1.0 : r);
        setUrgentNotifs(isUrgentNotificationsEnabled());
      } catch { /* ignore */ }
    } else {
      try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    }
  }, [open, initialTab]);

  // Auto-read the current tutorial step (only on tutorial tab, and only if first welcome)
  useEffect(() => {
    if (!open || tab !== "tutorial") return;
    try {
      const isFirst = !localStorage.getItem("welcome_voice_done_v1");
      if (!isFirst) return;
    } catch { /* ignore */ }
    const s = TUTORIAL[step];
    if (s) {
      const text = `${s.title}. ${s.body}`;
      const t = setTimeout(() => speakFR(text), 400);
      return () => clearTimeout(t);
    }
  }, [open, tab, step]);

  const markVoiceDone = () => {
    try { localStorage.setItem("welcome_voice_done_v1", "1"); } catch { /* ignore */ }
  };

  const toggleUrgentNotifs = async () => {
    if (urgentNotifs) {
      disableUrgentNotifications();
      setUrgentNotifs(false);
      toast.success("Notifications d'urgence désactivées");
    } else {
      const res = await enableUrgentNotifications();
      if (res.ok) {
        setUrgentNotifs(true);
        toast.success("Notifications d'urgence activées");
      } else if (res.reason === "denied") {
        toast.error("Autorisation refusée — activez-la dans les paramètres du navigateur");
      } else {
        toast.error("Notifications non supportées sur ce navigateur");
      }
    }
  };

  const saveAutoRead = (val) => {
    setAutoRead(val);
    try { localStorage.setItem("accessibility_auto_read", val ? "1" : "0"); } catch { /* ignore */ }
    window.dispatchEvent(new Event("accessibility-changed"));
  };

  const saveSpeechRate = (val) => {
    setSpeechRate(val);
    try { localStorage.setItem("accessibility_speech_rate", String(val)); } catch { /* ignore */ }
    window.dispatchEvent(new Event("accessibility-changed"));
  };

  const testVoice = () => {
    try {
      window.speechSynthesis?.cancel();
      const u = new SpeechSynthesisUtterance("Bonjour, ceci est un test de la lecture vocale d'Un Avocat en Or.");
      u.lang = "fr-BE";
      u.rate = speechRate;
      const voices = window.speechSynthesis.getVoices();
      const fr = voices.find((v) => v.lang?.toLowerCase().startsWith("fr")) || voices[0];
      if (fr) u.voice = fr;
      window.speechSynthesis.speak(u);
    } catch { /* ignore */ }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="welcome-onboarding" role="dialog" aria-labelledby="help-title">
      <div className="bg-white rounded-sm shadow-2xl max-w-lg w-full overflow-hidden max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 text-white p-5 sm:p-6 relative flex-shrink-0">
          <div className="absolute top-3 right-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => { try { window.speechSynthesis?.cancel(); markVoiceDone(); } catch { /* ignore */ } }}
              className="h-8 px-3 rounded-full bg-white/20 hover:bg-white/30 flex items-center gap-1.5 text-xs"
              data-testid="welcome-mute-voice"
              aria-label="Couper la voix"
            >
              <VolumeX className="h-3.5 w-3.5" /> Couper la voix
            </button>
            <button type="button" onClick={() => { try { window.speechSynthesis?.cancel(); } catch { /* ignore */ } onClose(); }} className="h-8 w-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center" data-testid="welcome-close" aria-label="Fermer">
              <X className="h-4 w-4" />
            </button>
          </div>
          <h2 id="help-title" className="font-serif-display text-2xl sm:text-3xl font-medium leading-tight pr-2">
            Aide & Accessibilité
          </h2>
        </div>

        {/* Tabs */}
        <div className="bg-slate-50 border-b border-slate-200 grid grid-cols-3 flex-shrink-0">
          <button
            type="button"
            onClick={() => setTab("tutorial")}
            className={`py-3 text-xs sm:text-sm font-medium transition-colors ${tab === "tutorial" ? "bg-white text-slate-900 border-b-2 border-amber-500" : "text-slate-500 hover:text-slate-900"}`}
            data-testid="help-tab-tutorial"
          >
            📖 Guide
          </button>
          <button
            type="button"
            onClick={() => setTab("accessibility")}
            className={`py-3 text-xs sm:text-sm font-medium transition-colors ${tab === "accessibility" ? "bg-white text-slate-900 border-b-2 border-amber-500" : "text-slate-500 hover:text-slate-900"}`}
            data-testid="help-tab-accessibility"
          >
            🔊 Accessibilité
          </button>
          <button
            type="button"
            onClick={() => setTab("cpas")}
            className={`py-3 text-xs sm:text-sm font-medium transition-colors ${tab === "cpas" ? "bg-white text-slate-900 border-b-2 border-amber-500" : "text-slate-500 hover:text-slate-900"}`}
            data-testid="help-tab-cpas"
          >
            📋 Pro / CPAS
          </button>
        </div>

        <div className="overflow-y-auto custom-scroll flex-1">
          {tab === "tutorial" && (
            <div className="p-6 sm:p-8" data-testid={`welcome-step-${step}`}>
              <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">Étape {step + 1} / {TUTORIAL.length}</p>
              {!TUTORIAL[step].hero && (
                <h3 className="font-serif-display text-xl font-medium leading-tight mb-3">{TUTORIAL[step].title}</h3>
              )}
              {/* Étape héros : blason officiel "Un Avocat en Or" sur fond noir */}
              {TUTORIAL[step].hero && (
                <div className="bg-[#0A0A0A] -mx-6 sm:-mx-8 mb-4 px-4 py-8 flex justify-center">
                  <img
                    src="/logo-seal-large.png"
                    alt="Un Avocat en Or"
                    width={280}
                    height={280}
                    style={{ width: "min(260px, 70vw)", height: "auto", display: "block" }}
                    data-testid="welcome-seal"
                  />
                </div>
              )}
              {TUTORIAL[step].visual && (
                <div className="text-center mb-4"><span className="text-5xl">{TUTORIAL[step].visual}</span></div>
              )}
              <p className="text-base text-slate-700 leading-relaxed mb-4">{TUTORIAL[step].body}</p>
              {TUTORIAL[step].permissions && <PermissionPrimer />}
              {TUTORIAL[step].tiles && (
                <div className="grid grid-cols-2 gap-2 mb-2">
                  {TUTORIAL[step].tiles.map((t, i) => {
                    const Icon = t.icon;
                    return (
                      <div key={i} className="border border-slate-200 rounded-sm p-3 flex items-start gap-2">
                        <div className="h-8 w-8 rounded-sm bg-slate-900 text-[#B89C65] flex items-center justify-center flex-shrink-0">
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-slate-900 leading-tight">{t.label}</p>
                          <p className="text-[10px] text-slate-500 leading-snug mt-0.5">{t.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tab === "accessibility" && (
            <div className="p-6 sm:p-8" data-testid="help-accessibility-panel">
              <h3 className="font-serif-display text-xl font-medium leading-tight mb-2">Paramètres de lecture vocale</h3>
              <p className="text-sm text-slate-600 mb-5">
                Optimisez l'application pour les utilisateurs malvoyants ou qui préfèrent écouter plutôt que lire.
              </p>

              {/* Toggle auto-read */}
              <div className="border border-slate-200 rounded-sm p-4 mb-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 mb-1">🔊 Lecture automatique des pages</p>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Active la lecture vocale dès qu'une nouvelle page s'affiche, sans devoir cliquer.
                      Idéal pour les seniors et malvoyants.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => saveAutoRead(!autoRead)}
                    className={`relative inline-flex h-7 w-12 flex-shrink-0 rounded-full transition-colors ${autoRead ? "bg-amber-500" : "bg-slate-300"}`}
                    role="switch"
                    aria-checked={autoRead}
                    aria-label="Activer la lecture automatique"
                    data-testid="toggle-auto-read"
                  >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform mt-1 ${autoRead ? "translate-x-6" : "translate-x-1"}`} />
                  </button>
                </div>
                {autoRead && (
                  <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-emerald-700 bg-emerald-50 rounded-sm p-2">
                    ✓ Mode lecture automatique activé sur toutes les pages
                  </div>
                )}
              </div>

              {/* Speech rate slider */}
              <div className="border border-slate-200 rounded-sm p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-slate-900">⚡ Vitesse de lecture</p>
                  <span className="text-sm font-mono text-slate-500" data-testid="speech-rate-value">{speechRate.toFixed(1)}×</span>
                </div>
                <input
                  type="range"
                  min="0.6"
                  max="1.6"
                  step="0.1"
                  value={speechRate}
                  onChange={(e) => saveSpeechRate(parseFloat(e.target.value))}
                  className="w-full accent-amber-500"
                  data-testid="speech-rate-slider"
                  aria-label="Vitesse de lecture vocale"
                />
                <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                  <span>Lent</span>
                  <span>Normal</span>
                  <span>Rapide</span>
                </div>
              </div>

              <Button
                type="button"
                onClick={testVoice}
                className="rounded-sm bg-slate-900 hover:bg-slate-800 h-10 w-full"
                data-testid="speech-test-button"
              >
                <Volume2 className="h-4 w-4 mr-2" /> Tester la voix
              </Button>

              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mt-5">
                <p className="text-xs text-amber-800 leading-relaxed">
                  💡 <strong>Astuce</strong> : sur mobile, branchez des écouteurs pour une écoute discrète,
                  notamment en cas de rendez-vous officiel ou en public.
                </p>
              </div>

              {/* Notifications d'urgence */}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <p className="text-xs uppercase tracking-widest text-slate-400 mb-3">🔔 Alertes</p>
                <div className="border border-slate-200 rounded-sm p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 mb-1 flex items-center gap-2">
                        <Bell className="h-4 w-4 text-amber-600" /> Notifications d'urgence uniquement
                      </p>
                      <p className="text-xs text-slate-500 leading-relaxed">
                        Vous recevrez UNIQUEMENT :<br/>
                        • Rappel avant effacement d'un enregistrement Bouclier (2h avant)<br/>
                        • Alerte arnaque nationale en cours (max 1-2 par mois)
                      </p>
                      <p className="text-xs text-emerald-700 mt-1.5">Aucun message marketing, aucune publicité.</p>
                    </div>
                    <button
                      type="button"
                      onClick={toggleUrgentNotifs}
                      className={`relative inline-flex h-7 w-12 flex-shrink-0 rounded-full transition-colors ${urgentNotifs ? "bg-amber-500" : "bg-slate-300"}`}
                      role="switch"
                      aria-checked={urgentNotifs}
                      aria-label="Activer les notifications d'urgence"
                      data-testid="toggle-urgent-notifs"
                    >
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform mt-1 ${urgentNotifs ? "translate-x-6" : "translate-x-1"}`} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === "cpas" && (
            <div className="p-6 sm:p-8" data-testid="help-cpas-panel">
              <h3 className="font-serif-display text-xl font-medium leading-tight mb-2">Dossier de présentation</h3>
              <p className="text-sm text-slate-600 mb-5">
                Pour les utilisateurs au CPAS ou demandeurs d'emploi qui veulent montrer ce projet à leur assistant social,
                à SMart, à microStart ou à un partenaire B2B. Téléchargez un PDF d'une page prêt à présenter.
              </p>

              <a
                href={(process.env.REACT_APP_BACKEND_URL || "") + "/api/public/dossier-cpas"}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-12 flex items-center justify-center gap-2 text-sm font-medium mb-5"
                data-testid="cpas-download-button"
              >
                <FileText className="h-4 w-4" /> Télécharger le dossier (PDF)
              </a>

              <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">Ressources utiles</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#B89C65] flex-shrink-0 mt-0.5">→</span>
                  <div>
                    <a href="https://smartbe.be" target="_blank" rel="noopener noreferrer" className="text-slate-900 font-medium underline-offset-2 hover:underline">SMart Coopérative</a>
                    <p className="text-xs text-slate-500">Facturation sans créer de société, ~6,5 % du CA</p>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#B89C65] flex-shrink-0 mt-0.5">→</span>
                  <div>
                    <a href="https://microstart.be" target="_blank" rel="noopener noreferrer" className="text-slate-900 font-medium underline-offset-2 hover:underline">microStart</a>
                    <p className="text-xs text-slate-500">Micro-crédit jusqu'à 25 000 € pour porteurs de projet</p>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#B89C65] flex-shrink-0 mt-0.5">→</span>
                  <div>
                    <a href="https://www.jobin.be" target="_blank" rel="noopener noreferrer" className="text-slate-900 font-medium underline-offset-2 hover:underline">Job'in</a>
                    <p className="text-xs text-slate-500">Accompagnement à la création d'activité (Bruxelles / Wallonie)</p>
                  </div>
                </li>
              </ul>

              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mt-5">
                <p className="text-xs text-amber-800 leading-relaxed">
                  ⚖️ <strong>Important</strong> : ce dossier est un support de présentation, pas un avis juridique.
                  Validez toujours votre situation avec un assistant social CPAS avant de générer des revenus.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {tab === "tutorial" && (
          <div className="p-5 sm:p-6 bg-slate-50 border-t border-slate-200 flex items-center justify-between flex-shrink-0">
            <div className="flex gap-1.5">
              {TUTORIAL.map((_, i) => (
                <span key={i} className={`h-2 rounded-full transition-all ${i === step ? "w-6 bg-amber-500" : "w-2 bg-slate-300"}`} />
              ))}
            </div>
            <div className="flex gap-2">
              {step > 0 && (
                <Button type="button" variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))} className="rounded-sm h-9 text-sm" data-testid="welcome-prev">
                  Retour
                </Button>
              )}
              <Button
                type="button"
                onClick={() => { if (step === TUTORIAL.length - 1) { markVoiceDone(); onClose(); } else setStep((s) => s + 1); }}
                className="rounded-sm bg-slate-900 hover:bg-slate-800 h-9 text-sm"
                data-testid="welcome-next"
              >
                {step === TUTORIAL.length - 1 ? "Commencer" : "Suivant"} <ArrowRight className="h-3.5 w-3.5 ml-2" />
              </Button>
            </div>
          </div>
        )}
        {tab === "accessibility" && (
          <div className="p-5 sm:p-6 bg-slate-50 border-t border-slate-200 flex justify-end flex-shrink-0">
            <Button type="button" onClick={onClose} className="rounded-sm bg-slate-900 hover:bg-slate-800 h-9 text-sm" data-testid="help-close-button">
              Fermer
            </Button>
          </div>
        )}
        {tab === "cpas" && (
          <div className="p-5 sm:p-6 bg-slate-50 border-t border-slate-200 flex justify-end flex-shrink-0">
            <Button type="button" onClick={onClose} className="rounded-sm bg-slate-900 hover:bg-slate-800 h-9 text-sm" data-testid="help-close-button-cpas">
              Fermer
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function PermissionPrimer() {
  const [mic, setMic] = useState("idle"); // idle | granted | denied
  const [geo, setGeo] = useState("idle");

  const reqMic = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      setMic("granted");
      toast.success("Micro autorisé !");
    } catch {
      setMic("denied");
      toast.error("Permission refusée. Allez dans Réglages iOS → Safari pour l'autoriser.");
    }
  };

  const reqGeo = () => {
    if (!navigator.geolocation) { setGeo("denied"); return; }
    navigator.geolocation.getCurrentPosition(
      () => { setGeo("granted"); toast.success("Géolocalisation autorisée !"); },
      () => { setGeo("denied"); toast.error("Permission refusée."); },
      { timeout: 8000 }
    );
  };

  const reqBoth = async () => {
    await reqMic();
    reqGeo();
  };

  const allReady = mic === "granted" && geo === "granted";

  return (
    <div className="bg-red-50 border-2 border-red-600 rounded-sm p-4 mb-4" data-testid="permission-primer">
      <p className="text-sm font-bold text-red-700 uppercase tracking-wider mb-3 text-center">⚡ À faire MAINTENANT</p>

      <button
        type="button"
        onClick={reqBoth}
        disabled={allReady}
        className={`w-full h-14 rounded-sm font-medium text-base ${allReady ? "bg-emerald-600 text-white" : "bg-red-600 hover:bg-red-700 text-white"} flex items-center justify-center gap-2 mb-3 shadow-md`}
        data-testid="permission-grant-all"
      >
        {allReady ? "✓ Téléphone prêt pour l'urgence" : "🔓 Préparer le téléphone (micro + géolocalisation)"}
      </button>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className={`text-center p-2 rounded-sm ${mic === "granted" ? "bg-emerald-100 text-emerald-800" : mic === "denied" ? "bg-red-100 text-red-800" : "bg-white text-slate-600"}`}>
          🎙️ Micro<br/><strong>{mic === "granted" ? "✓ OK" : mic === "denied" ? "✗ Refusé" : "En attente"}</strong>
        </div>
        <div className={`text-center p-2 rounded-sm ${geo === "granted" ? "bg-emerald-100 text-emerald-800" : geo === "denied" ? "bg-red-100 text-red-800" : "bg-white text-slate-600"}`}>
          📍 Géoloc<br/><strong>{geo === "granted" ? "✓ OK" : geo === "denied" ? "✗ Refusé" : "En attente"}</strong>
        </div>
      </div>

      {(mic === "denied" || geo === "denied") && (
        <p className="text-xs text-red-700 mt-3 leading-snug">
          ℹ️ Si refusé par erreur : Réglages iOS → Safari → Avancé → Données du site → autoriser micro et localisation pour cette app.
        </p>
      )}
    </div>
  );
}

